import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import {handleApiError} from '@/utils/errorHandler.ts';
import {push} from 'notivue';

export interface OutdoorsyState {
    loading: Record<string, boolean>;
    form: Form<any>;
    connections: any[];
    listings: any[];
    bookings: any[];
}

const state: OutdoorsyState = {
    loading: {
        login: false,
        verifyTwoFactor: false,
        matching: false,
        toggle: false,
        delete: false,
        listings: false,
    },
    form: new Form(),
    connections: [],
    listings: [],
    bookings: [],
};

const getters = {
    form: (state: OutdoorsyState) => state.form,
    connections: (state: OutdoorsyState) => state.connections,
    loading: (state: OutdoorsyState) => state.loading,
    listings: (state: OutdoorsyState) => state.listings,
    bookings: (state: OutdoorsyState) => state.bookings,
};

const mutations = {
    [types.SET_OUTDOORSY_LOADING](state: OutdoorsyState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_OUTDOORSY_CONNECTIONS](state: OutdoorsyState, connections) {
        state.connections = connections;
    },
    [types.SET_OUTDOORSY_LISTINGS](state: OutdoorsyState, listings) {
        state.listings = listings;
    },
    [types.SET_OUTDOORSY_BOOKINGS](state: OutdoorsyState, bookings) {
        state.bookings = bookings;
    },
};

const actions = {

    /**
     * Login to Outdoorsy
     */
    async login({commit}, credentials: { email: string, password: string }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'login', () => axios.post(`/v1/connections/outdoorsy`, credentials));
            if (responseData?.two_factor_required || responseData?.['2fa_required']) {
                push.info({message: responseData?.message ?? 'Please check your email for the Outdoorsy 2FA code.'});

                return responseData;
            }

            commit('SET_OUTDOORSY_CONNECTIONS', responseData?.providers);
            push.success({message: 'Outdoorsy connection connected successfully!'});

            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error connecting to Outdoorsy.');
            throw error;
        }
    },


    /**
     * Verify an Outdoorsy 2FA code.
     */
    async verifyTwoFactor({commit}, data: { email: string, password: string, session_id_2fa: string, code: string }) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'verifyTwoFactor', () => axios.post(`/v1/connections/outdoorsy/2fa/verify`, data));
            commit('SET_OUTDOORSY_CONNECTIONS', responseData?.providers);
            push.success({message: 'Outdoorsy connection connected successfully!'});

            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error verifying your Outdoorsy 2FA code.');
            throw error;
        }
    },


    /**
     * Delete Outdoorsy
     */
    async delete({commit}, outdoorsy) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'delete', () => axios.delete(`/v1/connections/outdoorsy/${outdoorsy.uuid}`));
            commit('SET_OUTDOORSY_CONNECTIONS', responseData.providers);
            push.success({message: 'Outdoorsy connection deleted successfully!'});
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Toggle Outdoorsy active
     */
    async toggleActive({commit}, outdoorsy) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'toggle', () => axios.put(`/v1/connections/outdoorsy/${outdoorsy.uuid}/active`));
            commit('SET_OUTDOORSY_LISTINGS', responseData?.listings);
            push.success({message: 'Outdoorsy connection toggled successfully!'});
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Fetch Outdoorsy listings
     */
    async fetchOutdoorsyListings({commit}, outdoorsy_uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'listings', () => axios.get(`/v1/connections/outdoorsy/${outdoorsy_uuid}/vehicles`));
            commit('SET_OUTDOORSY_LISTINGS', responseData?.listings);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Fetch Outdoorsy bookings
     */
    async fetchBookings({commit}, outdoorsy_uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_OUTDOORSY_LOADING', 'listings', () => axios.get(`/v1/connections/outdoorsy/${outdoorsy_uuid}/bookings`));
            commit('SET_OUTDOORSY_BOOKINGS', responseData?.listings);
        } catch (error) {
            handleApiError(error);
        }
    },

};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
